const BRAZIL_COUNTRY_CODE = '55';

export type TaxDocumentType = 'CPF' | 'CNPJ';

function digitsOf(value: string | null | undefined): string {
    return (value ?? '').replace(/\D/g, '');
}

/**
 * A Brazilian phone as people write it: (62) 99999-1234. The API stores digits only, with the
 * country code on WhatsApp numbers; anything that is not a 10- or 11-digit number comes back as is.
 */
export function formatPhoneNumber(value: string | null | undefined): string {
    let digits = digitsOf(value);

    if (digits.startsWith(BRAZIL_COUNTRY_CODE) && digits.length >= 12) {
        digits = digits.slice(BRAZIL_COUNTRY_CODE.length);
    }

    if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    return value ?? '';
}

export function taxDocumentType(value: string | null | undefined): TaxDocumentType | null {
    switch (digitsOf(value).length) {
        case 11:
            return 'CPF';
        case 14:
            return 'CNPJ';
        default:
            return null;
    }
}

/** 000.000.000-00 for a CPF, 00.000.000/0000-00 for a CNPJ; anything else comes back as is. */
export function formatTaxDocument(value: string | null | undefined): string {
    const digits = digitsOf(value);

    switch (taxDocumentType(digits)) {
        case 'CPF':
            return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
        case 'CNPJ':
            return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/`
                + `${digits.slice(8, 12)}-${digits.slice(12)}`;
        default:
            return value ?? '';
    }
}

/** The document with the name of its kind in front, as it reads in a sentence: "CPF 000.000.000-00". */
export function describeTaxDocument(value: string | null | undefined): string {
    const formatted = formatTaxDocument(value);
    const type = taxDocumentType(value);

    if (!formatted) {
        return '';
    }

    return `${type ?? 'CPF/CNPJ'} ${formatted}`;
}

/** "Goiânia/GO", or whichever of the two is known; nothing when neither is. */
export function formatCityAndState(city: string | null | undefined, state: string | null | undefined): string {
    return [city, state].map(part => part?.trim()).filter(Boolean).join('/');
}

/** First letter of the first and of the last name: "Mário da Silva Pinheiro" is "MP". */
export function nameInitials(name: string | null | undefined): string {
    const words = (name ?? '').trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) {
        return '';
    }

    const first = words[0].charAt(0);
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';

    return (first + last).toLocaleUpperCase('pt-BR');
}
